// Roles & permissions for a vendor's staff (lab and pharmacy).
// No roles API exists yet, so roles are kept per vendor in this browser.
// When the backend adds one, replace loadRoles/saveRoles with http calls;
// the page only depends on these functions and types.

export const OPERATIONS = ["view", "add", "edit", "delete", "export"] as const;
export type Operation = typeof OPERATIONS[number];

export const OPERATION_LABELS: Record<Operation, string> = {
  view: "View",
  add: "Add",
  edit: "Edit",
  delete: "Delete",
  export: "Export",
};

export interface PermissionModule {
  key: string;
  label: string;
  // Operations that make sense for this module; others are shown as not applicable
  operations: Operation[];
}

const ALL: Operation[] = [...OPERATIONS];
const VIEW_ONLY: Operation[] = ["view"];
const VIEW_EDIT: Operation[] = ["view", "edit"];

// Modules mirror the sidebar of each vendor type
const LAB_MODULES: PermissionModule[] = [
  { key: "overview", label: "Lab Overview", operations: VIEW_ONLY },
  { key: "orders", label: "Orders & Order History", operations: ["view", "edit", "export"] },
  { key: "labTests", label: "Lab Tests", operations: ALL },
  { key: "labPackages", label: "Lab Packages", operations: ALL },
  { key: "schedules", label: "Schedules", operations: ALL },
  { key: "revenue", label: "Revenue & Payment", operations: ["view", "export"] },
  { key: "technicians", label: "Lab Technician Management", operations: ALL },
  { key: "reports", label: "Lab Report", operations: ALL },
  { key: "labCategory", label: "Lab Category", operations: ALL },
  { key: "scanning", label: "Scanning", operations: ALL },
  { key: "notifications", label: "Notifications", operations: VIEW_ONLY },
  { key: "settings", label: "Settings", operations: VIEW_EDIT },
  { key: "roles", label: "Roles & Permissions", operations: ["view", "add", "edit", "delete"] },
];

const PHARMACY_MODULES: PermissionModule[] = [
  { key: "overview", label: "Overview", operations: VIEW_ONLY },
  { key: "orders", label: "Pharmacy Orders & Order History", operations: ["view", "edit", "export"] },
  { key: "payments", label: "Payment", operations: ["view", "export"] },
  { key: "products", label: "Products", operations: ALL },
  { key: "settings", label: "Settings", operations: VIEW_EDIT },
  { key: "roles", label: "Roles & Permissions", operations: ["view", "add", "edit", "delete"] },
];

export const getPermissionModules = (isPharmacy: boolean) => (isPharmacy ? PHARMACY_MODULES : LAB_MODULES);

export type PermissionMatrix = Record<string, Partial<Record<Operation, boolean>>>;

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: PermissionMatrix;
  // Built-in Administrator: always has every permission and can't be changed or deleted
  isSystem?: boolean;
  updatedAt: string;
}

export const buildMatrix = (
  modules: PermissionModule[],
  grant: (module: PermissionModule, op: Operation) => boolean
): PermissionMatrix =>
  modules.reduce((matrix, module) => {
    matrix[module.key] = module.operations.reduce((ops, op) => ({ ...ops, [op]: grant(module, op) }), {});
    return matrix;
  }, {} as PermissionMatrix);

const now = () => new Date().toISOString();

const defaultRoles = (isPharmacy: boolean): Role[] => {
  const modules = getPermissionModules(isPharmacy);
  const viewOnly = (keys: string[]) => (m: PermissionModule, op: Operation) => keys.includes(m.key) && op === "view";
  const full = (keys: string[]) => (m: PermissionModule) => keys.includes(m.key);

  const admin: Role = {
    id: "administrator",
    name: "Administrator",
    description: "Full access to everything, including roles.",
    permissions: buildMatrix(modules, () => true),
    isSystem: true,
    updatedAt: now(),
  };

  if (isPharmacy) {
    return [
      admin,
      {
        id: "pharmacist",
        name: "Pharmacist",
        description: "Handles orders and keeps products up to date.",
        permissions: buildMatrix(modules, (m, op) => full(["orders", "products"])(m) || viewOnly(["overview"])(m, op)),
        updatedAt: now(),
      },
      {
        id: "billing",
        name: "Billing",
        description: "Views orders and payments, exports reports.",
        permissions: buildMatrix(
          modules,
          (m, op) => (["orders", "payments"].includes(m.key) && (op === "view" || op === "export")) || viewOnly(["overview"])(m, op)
        ),
        updatedAt: now(),
      },
    ];
  }

  return [
    admin,
    {
      id: "front-desk",
      name: "Front Desk",
      description: "Manages orders, schedules and assigns technicians.",
      permissions: buildMatrix(
        modules,
        (m, op) => full(["orders", "schedules"])(m) || viewOnly(["overview", "labTests", "labPackages", "technicians", "notifications"])(m, op)
      ),
      updatedAt: now(),
    },
    {
      id: "lab-technician",
      name: "Lab Technician",
      description: "Views assigned orders and uploads reports.",
      permissions: buildMatrix(
        modules,
        (m, op) => (m.key === "reports" && op !== "delete") || viewOnly(["orders", "labTests", "notifications"])(m, op)
      ),
      updatedAt: now(),
    },
  ];
};

const storageKey = (vendorId: string) => `roles:${vendorId}`;

// Keeps stored roles in step with the current module list (new modules start without access)
const normalizeRole = (role: Role, modules: PermissionModule[]): Role => ({
  ...role,
  permissions: buildMatrix(modules, (m, op) => (role.isSystem ? true : !!role.permissions?.[m.key]?.[op])),
});

export const loadRoles = async (vendorId: string, isPharmacy: boolean): Promise<Role[]> => {
  const modules = getPermissionModules(isPharmacy);
  try {
    const raw = localStorage.getItem(storageKey(vendorId));
    const roles: Role[] = raw ? JSON.parse(raw) : defaultRoles(isPharmacy);
    return roles.map((role) => normalizeRole(role, modules));
  } catch {
    return defaultRoles(isPharmacy);
  }
};

export const saveRoles = async (vendorId: string, roles: Role[]): Promise<void> => {
  localStorage.setItem(storageKey(vendorId), JSON.stringify(roles));
};

export const countGranted = (matrix: PermissionMatrix) =>
  Object.values(matrix).reduce((sum, ops) => sum + Object.values(ops).filter(Boolean).length, 0);

export const countAvailable = (modules: PermissionModule[]) =>
  modules.reduce((sum, m) => sum + m.operations.length, 0);

export const newRoleId = (name: string) =>
  `${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "role"}-${Date.now().toString(36)}`;
